import React, { useMemo, useState } from "react";
import { ChevronRight, Folder, FolderOpen, Library, Trash2 } from "lucide-react";
import { cx } from "../../../pages/admin/_shared";
import { fmtBytes } from "./mediaUtils";

/** Cây thư mục dựng từ danh sách phẳng [{prefix,name,count}]. */
function build(flat) {
  const nodes = new Map(flat.map((n) => [n.prefix, { ...n, children: [] }]));
  const roots = [];
  for (const n of nodes.values()) {
    const parent = n.prefix.split("/").slice(0, -2).join("/");
    const p = parent ? nodes.get(`${parent}/`) : null;
    (p ? p.children : roots).push(n);
  }
  return roots;
}

function Node({ node, depth, current, onOpen, openSet, toggle }) {
  const isOpen = openSet.has(node.prefix) || current.startsWith(node.prefix);
  const active = current === node.prefix;
  const has = node.children.length > 0;
  return (
    <li>
      <div
        className={cx(
          "group flex items-center gap-1 rounded-lg pr-2 text-[13px] transition",
          active ? "bg-admin-primaryLight font-semibold text-admin-primary" : "text-admin-text hover:bg-admin-muted"
        )}
        style={{ paddingLeft: 4 + depth * 14 }}
      >
        <button type="button" onClick={() => has && toggle(node.prefix)} className={cx("flex h-6 w-5 items-center justify-center text-admin-textMuted", !has && "invisible")} aria-label={isOpen ? "Thu gọn" : "Mở rộng"}>
          <ChevronRight size={13} className={cx("transition", isOpen && "rotate-90")} />
        </button>
        <button type="button" onClick={() => onOpen(node.prefix)} className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left">
          {active || isOpen ? <FolderOpen size={15} className="shrink-0" /> : <Folder size={15} className="shrink-0 text-amber-500" />}
          <span className="truncate">{node.name}</span>
        </button>
        <span className="text-[11px] text-admin-textMuted">{node.count}</span>
      </div>
      {has && isOpen ? (
        <ul>
          {node.children.map((c) => (
            <Node key={c.prefix} node={c} depth={depth + 1} current={current} onOpen={onOpen} openSet={openSet} toggle={toggle} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function FolderTree({ tree, current, onOpen, total, trashCount, inTrash, onOpenTrash, totalSize }) {
  const roots = useMemo(() => build(tree || []), [tree]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const toggle = (p) =>
    setOpenSet((s) => {
      const n = new Set(s);
      n.has(p) ? n.delete(p) : n.add(p);
      return n;
    });

  return (
    <nav aria-label="Thư mục" className="space-y-1">
      <button
        type="button"
        onClick={() => onOpen("")}
        className={cx(
          "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition",
          !inTrash && current === "" ? "bg-admin-primaryLight font-semibold text-admin-primary" : "text-admin-text hover:bg-admin-muted"
        )}
      >
        <Library size={15} />
        <span className="flex-1 text-left">Tất cả tệp</span>
        <span className="text-[11px] text-admin-textMuted">{total ?? ""}</span>
      </button>
      <ul className="space-y-0.5">
        {roots.map((n) => (
          <Node key={n.prefix} node={n} depth={0} current={inTrash ? "\u0000" : current} onOpen={onOpen} openSet={openSet} toggle={toggle} />
        ))}
      </ul>
      <div className="my-2 border-t border-admin-border" />
      <button
        type="button"
        onClick={onOpenTrash}
        className={cx("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition", inTrash ? "bg-rose-50 font-semibold text-rose-700" : "text-admin-text hover:bg-admin-muted")}
      >
        <Trash2 size={15} />
        <span className="flex-1 text-left">Thùng rác</span>
        {trashCount ? <span className="rounded-full bg-rose-100 px-1.5 text-[11px] font-semibold text-rose-700">{trashCount}</span> : null}
      </button>
      {totalSize != null ? <p className="px-2 pt-3 text-[11px] text-admin-textMuted">Dung lượng đang dùng: {fmtBytes(totalSize)}</p> : null}
    </nav>
  );
}
